import { useState, useRef, useCallback, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Slider } from "@/components/ui/slider";
import { Progress } from "@/components/ui/progress";
import { 
  Upload, Image, Video, X, Trash2, Loader2, Move, ZoomIn, RotateCw, Crop, Check
} from "lucide-react";
import { uploadFile, resolveImageUrl } from "@/lib/api";
import { toast } from "sonner";
import ReactCrop from 'react-image-crop';
import 'react-image-crop/dist/ReactCrop.css';

// Generate video thumbnail from first frame
const generateVideoThumbnail = (videoFile) => {
  return new Promise((resolve) => {
    const video = document.createElement('video');
    video.preload = 'metadata';
    video.muted = true;
    video.playsInline = true;
    
    video.onloadeddata = () => {
      video.currentTime = 1; // Seek to 1 second
    };
    
    video.onseeked = () => {
      const canvas = document.createElement('canvas');
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
      const thumbnail = canvas.toDataURL('image/jpeg', 0.7);
      URL.revokeObjectURL(video.src);
      resolve(thumbnail);
    };
    
    video.onerror = () => {
      resolve(null);
    };
    
    video.src = URL.createObjectURL(videoFile);
  });
};

// Image Cropper Modal
function ImageCropperModal({ isOpen, onClose, imageFile, onSave }) {
  const [crop, setCrop] = useState({ unit: '%', width: 80, height: 80, x: 10, y: 10 });
  const [completedCrop, setCompletedCrop] = useState(null);
  const [rotation, setRotation] = useState(0);
  const [zoom, setZoom] = useState(1);
  const [loading, setLoading] = useState(false);
  const imgRef = useRef(null);
  const [imageSrc, setImageSrc] = useState(null);

  // Load image when file changes
  useEffect(() => {
    if (imageFile) {
      const reader = new FileReader();
      reader.onload = () => setImageSrc(reader.result);
      reader.readAsDataURL(imageFile);
    }
  }, [imageFile]);

  const getCroppedImg = useCallback(async () => {
    if (!imgRef.current || !completedCrop) return null;

    const image = imgRef.current;
    const canvas = document.createElement('canvas');
    const scaleX = image.naturalWidth / image.width;
    const scaleY = image.naturalHeight / image.height;
    
    canvas.width = completedCrop.width * scaleX;
    canvas.height = completedCrop.height * scaleY;
    
    const ctx = canvas.getContext('2d');
    
    // Apply rotation if needed
    if (rotation !== 0) {
      ctx.translate(canvas.width / 2, canvas.height / 2);
      ctx.rotate((rotation * Math.PI) / 180);
      ctx.translate(-canvas.width / 2, -canvas.height / 2);
    }
    
    ctx.drawImage(
      image,
      completedCrop.x * scaleX,
      completedCrop.y * scaleY,
      completedCrop.width * scaleX,
      completedCrop.height * scaleY,
      0,
      0,
      canvas.width,
      canvas.height
    );

    return new Promise((resolve) => {
      canvas.toBlob((blob) => {
        resolve(blob);
      }, 'image/jpeg', 0.9);
    });
  }, [completedCrop, rotation]);

  const handleSave = async () => {
    setLoading(true);
    try {
      const croppedBlob = await getCroppedImg();
      if (croppedBlob) {
        const croppedFile = new File([croppedBlob], imageFile.name, { type: 'image/jpeg' });
        onSave(croppedFile);
      } else {
        // No crop, use original
        onSave(imageFile);
      }
      onClose();
    } catch (err) {
      toast.error("Failed to process image");
    }
    setLoading(false);
  };

  const handleSkipCrop = () => {
    onSave(imageFile);
    onClose();
  };

  if (!imageSrc) return null;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="bg-white border-[#E5E0D5] max-w-2xl">
        <DialogHeader>
          <DialogTitle className="font-['Crimson_Text'] text-xl text-[#2D2A26] flex items-center gap-2">
            <Crop className="w-5 h-5 text-[var(--liturgical-primary)]" />
            Edit Image
          </DialogTitle>
        </DialogHeader>
        
        <div className="space-y-4">
          {/* Crop Area */}
          <div className="flex justify-center bg-[#F9F7F2] rounded-lg p-4 max-h-[400px] overflow-auto">
            <ReactCrop
              crop={crop}
              onChange={(c) => setCrop(c)}
              onComplete={(c) => setCompletedCrop(c)}
              aspect={undefined}
            >
              <img
                ref={imgRef}
                src={imageSrc}
                alt="Crop preview"
                style={{ 
                  maxHeight: '350px',
                  transform: `rotate(${rotation}deg) scale(${zoom})`,
                  transition: 'transform 0.2s'
                }}
              />
            </ReactCrop>
          </div>
          
          {/* Controls */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <label className="text-xs text-[#6B665E] font-['Nunito'] flex items-center gap-1">
                <ZoomIn className="w-3 h-3" /> Zoom
              </label>
              <Slider
                value={[zoom * 100]}
                onValueChange={([v]) => setZoom(v / 100)}
                min={50}
                max={200}
                step={5}
                className="w-full"
              />
            </div>
            <div className="space-y-2">
              <label className="text-xs text-[#6B665E] font-['Nunito'] flex items-center gap-1">
                <RotateCw className="w-3 h-3" /> Rotation
              </label>
              <div className="flex gap-2">
                <Button size="sm" variant="outline" onClick={() => setRotation(r => r - 90)} className="flex-1">
                  -90°
                </Button>
                <Button size="sm" variant="outline" onClick={() => setRotation(0)} className="flex-1">
                  Reset
                </Button>
                <Button size="sm" variant="outline" onClick={() => setRotation(r => r + 90)} className="flex-1">
                  +90°
                </Button>
              </div>
            </div>
          </div>
        </div>
        
        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={handleSkipCrop} className="border-[#E5E0D5]">
            Skip Editing
          </Button>
          <Button onClick={handleSave} disabled={loading} className="bg-[var(--liturgical-primary)] hover:brightness-90 text-white">
            {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Check className="w-4 h-4 mr-2" />}
            Apply & Upload
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// Main Media Uploader Component
export default function MediaUploader({
  photos = [],
  videos = [],
  onPhotosChange,
  onVideosChange,
  maxPhotos = 10,
  maxVideos = 3,
  enableCropping = true
}) {
  const [uploadingMedia, setUploadingMedia] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isDraggingPhoto, setIsDraggingPhoto] = useState(false);
  const [isDraggingVideo, setIsDraggingVideo] = useState(false);
  const [cropperOpen, setCropperOpen] = useState(false);
  const [imageToEdit, setImageToEdit] = useState(null);
  const [pendingImages, setPendingImages] = useState([]);
  
  const photoInputRef = useRef(null);
  const videoInputRef = useRef(null);

  // Handle drag events for photos
  const handlePhotoDragOver = (e) => {
    e.preventDefault();
    setIsDraggingPhoto(true);
  };

  const handlePhotoDragLeave = (e) => {
    e.preventDefault();
    setIsDraggingPhoto(false);
  };

  const handlePhotoDrop = (e) => {
    e.preventDefault();
    setIsDraggingPhoto(false);
    const files = Array.from(e.dataTransfer.files).filter(f => f.type.startsWith('image/'));
    if (files.length > 0) {
      processImageFiles(files);
    }
  };

  // Handle drag events for videos
  const handleVideoDragOver = (e) => {
    e.preventDefault();
    setIsDraggingVideo(true);
  };

  const handleVideoDragLeave = (e) => {
    e.preventDefault();
    setIsDraggingVideo(false);
  };

  const handleVideoDrop = (e) => {
    e.preventDefault();
    setIsDraggingVideo(false);
    const allowedTypes = ['video/mp4', 'video/webm', 'video/quicktime', 'video/x-msvideo'];
    const files = Array.from(e.dataTransfer.files).filter(f => allowedTypes.includes(f.type));
    if (files.length > 0) {
      handleVideoUpload(files);
    }
  };

  // Process image files (with optional cropping)
  const processImageFiles = (files) => {
    if (photos.length + files.length > maxPhotos) {
      toast.error(`Maximum ${maxPhotos} photos allowed`);
      return;
    }

    if (enableCropping && files.length > 0) {
      setPendingImages(files);
      setImageToEdit(files[0]);
      setCropperOpen(true);
    } else {
      uploadImages(files);
    }
  };

  // Handle cropped/edited image
  const handleCroppedImage = async (file) => {
    await uploadSingleImage(file);
    
    // Process next pending image
    const remaining = pendingImages.slice(1);
    if (remaining.length > 0) {
      setPendingImages(remaining);
      setImageToEdit(remaining[0]);
      setCropperOpen(true);
    } else {
      setPendingImages([]);
      setImageToEdit(null);
    }
  };

  // Upload single image
  const uploadSingleImage = async (file) => {
    if (!file.type.startsWith('image/')) {
      toast.error(`${file.name} is not an image`);
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      toast.error(`${file.name} is too large. Max 10MB.`);
      return;
    }

    setUploadingMedia(true);
    try {
      const res = await uploadFile(file, (progress) => setUploadProgress(progress));
      onPhotosChange([...photos, { url: res.data.url, name: file.name }]);
      toast.success("Photo uploaded");
    } catch (err) {
      toast.error("Failed to upload photo");
    }
    setUploadingMedia(false);
    setUploadProgress(0);
  };

  // Upload multiple images without cropping
  const uploadImages = async (files) => {
    setUploadingMedia(true);
    setUploadProgress(0);
    
    const newPhotos = [];
    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      if (!file.type.startsWith('image/')) continue;
      if (file.size > 10 * 1024 * 1024) continue;
      
      try {
        const res = await uploadFile(file, (progress) => {
          setUploadProgress(Math.round((i / files.length) * 100 + progress / files.length));
        });
        newPhotos.push({ url: res.data.url, name: file.name });
      } catch (err) {
        console.error("Upload failed:", err);
      }
    }
    
    if (newPhotos.length > 0) {
      onPhotosChange([...photos, ...newPhotos]);
      toast.success(`${newPhotos.length} photo(s) uploaded`);
    }
    
    setUploadingMedia(false);
    setUploadProgress(0);
  };

  // Handle video upload with thumbnail generation
  const handleVideoUpload = async (files) => {
    if (videos.length + files.length > maxVideos) {
      toast.error(`Maximum ${maxVideos} videos allowed`);
      return;
    }

    setUploadingMedia(true);
    setUploadProgress(0);
    
    const newVideos = [];
    const allowedTypes = ['video/mp4', 'video/webm', 'video/quicktime', 'video/x-msvideo'];
    
    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      if (!allowedTypes.includes(file.type)) {
        toast.error(`${file.name} is not a supported video format`);
        continue;
      }
      if (file.size > 1024 * 1024 * 1024) {
        toast.error(`${file.name} is too large. Max 1GB.`);
        continue;
      }
      
      try {
        // Upload video first - server will generate thumbnail and detect duration
        const res = await uploadFile(file, (progress) => {
          setUploadProgress(Math.round((i / files.length) * 100 + progress / files.length));
        });
        
        // Use server-generated thumbnail if available, otherwise fall back to client-side generation
        let thumbnail = res.data.thumbnail;
        if (!thumbnail) {
          // Server thumbnail failed, try client-side generation
          thumbnail = await generateVideoThumbnail(file);
        } else {
          // Convert server thumbnail URL to absolute URL
          thumbnail = resolveImageUrl(thumbnail);
        }
        
        newVideos.push({ 
          url: res.data.url, 
          name: file.name,
          thumbnail: thumbnail,
          duration: res.data.duration || null,
          duration_formatted: res.data.duration_formatted || null
        });
      } catch (err) {
        console.error("Video upload failed:", err);
      }
    }
    
    if (newVideos.length > 0) {
      onVideosChange([...videos, ...newVideos]);
      toast.success(`${newVideos.length} video(s) uploaded`);
    }
    
    setUploadingMedia(false);
    setUploadProgress(0);
  };

  const handlePhotoInputChange = (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length > 0) processImageFiles(files);
    if (photoInputRef.current) photoInputRef.current.value = '';
  };

  const handleVideoInputChange = (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length > 0) handleVideoUpload(files);
    if (videoInputRef.current) videoInputRef.current.value = '';
  };

  const removePhoto = (index) => onPhotosChange(photos.filter((_, i) => i !== index));
  const removeVideo = (index) => onVideosChange(videos.filter((_, i) => i !== index));

  return (
    <div className="space-y-4">
      {/* Photos Section */}
      <div className="space-y-2">
        <label className="font-['Nunito'] text-xs text-[#2D2A26] font-medium flex items-center gap-2">
          <Image className="w-4 h-4" /> Photos (up to {maxPhotos})
        </label>
        
        {/* Photo Previews */}
        {photos.length > 0 && (
          <div className="grid grid-cols-4 gap-2">
            {photos.map((photo, idx) => (
              <div key={idx} className="relative group aspect-square">
                <img 
                  src={resolveImageUrl(photo.url)} 
                  alt={`Photo ${idx + 1}`}
                  className="w-full h-full object-cover rounded-lg border border-[#E5E0D5]"
                />
                <button
                  onClick={() => removePhoto(idx)}
                  className="absolute -top-1 -right-1 bg-red-500 text-white rounded-full p-0.5 opacity-0 group-hover:opacity-100 transition-opacity"
                >
                  <X className="w-3 h-3" />
                </button>
              </div>
            ))}
          </div>
        )}
        
        {/* Photo Drop Zone */}
        <input
          ref={photoInputRef}
          type="file"
          accept="image/*"
          multiple
          onChange={handlePhotoInputChange}
          className="hidden"
        />
        <div
          onDragOver={handlePhotoDragOver}
          onDragLeave={handlePhotoDragLeave}
          onDrop={handlePhotoDrop}
          onClick={() => photoInputRef.current?.click()}
          className={`border-2 border-dashed rounded-lg p-4 text-center cursor-pointer transition-all ${
            isDraggingPhoto 
              ? 'border-[var(--liturgical-primary)] bg-[var(--liturgical-primary)]/5' 
              : 'border-[#E5E0D5] hover:border-[var(--liturgical-primary)] hover:bg-[#F9F7F2]'
          } ${uploadingMedia || photos.length >= maxPhotos ? 'opacity-50 pointer-events-none' : ''}`}
          data-testid="photo-dropzone"
        >
          <Upload className={`w-6 h-6 mx-auto mb-2 ${isDraggingPhoto ? 'text-[var(--liturgical-primary)]' : 'text-[#6B665E]'}`} />
          <p className="text-sm text-[#6B665E] font-['Nunito']">
            {isDraggingPhoto ? 'Drop photos here' : 'Drag & drop photos or click to browse'}
          </p>
          <p className="text-xs text-[#6B665E]/70 font-['Nunito'] mt-1">
            JPEG, PNG, GIF, WebP • Max 10MB each
          </p>
        </div>
      </div>

      {/* Videos Section */}
      <div className="space-y-2">
        <label className="font-['Nunito'] text-xs text-[#2D2A26] font-medium flex items-center gap-2">
          <Video className="w-4 h-4" /> Videos (up to {maxVideos}, max 100MB each)
        </label>
        
        {/* Video Previews with Thumbnails */}
        {videos.length > 0 && (
          <div className="grid grid-cols-2 gap-2">
            {videos.map((video, idx) => (
              <div key={idx} className="relative group rounded-lg overflow-hidden bg-[#F9F7F2] border border-[#E5E0D5]">
                {video.thumbnail ? (
                  <img 
                    src={video.thumbnail} 
                    alt={`Video ${idx + 1} thumbnail`}
                    className="w-full aspect-video object-cover"
                  />
                ) : (
                  <div className="w-full aspect-video flex items-center justify-center bg-gray-100">
                    <Video className="w-8 h-8 text-[#6B665E]" />
                  </div>
                )}
                <div className="absolute inset-0 bg-black/30 flex items-center justify-center">
                  <div className="w-10 h-10 rounded-full bg-white/90 flex items-center justify-center">
                    <Video className="w-5 h-5 text-[var(--liturgical-primary)]" />
                  </div>
                </div>
                {/* Duration Badge */}
                {video.duration_formatted && (
                  <div className="absolute bottom-8 right-2 bg-black/80 px-1.5 py-0.5 rounded text-[10px] text-white font-['Nunito'] font-medium">
                    {video.duration_formatted}
                  </div>
                )}
                <div className="absolute bottom-0 left-0 right-0 bg-black/50 px-2 py-1">
                  <p className="text-xs text-white truncate font-['Nunito']">{video.name}</p>
                </div>
                <button
                  onClick={() => removeVideo(idx)}
                  className="absolute top-1 right-1 bg-red-500 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                >
                  <Trash2 className="w-3 h-3" />
                </button>
              </div>
            ))}
          </div>
        )}
        
        {/* Video Drop Zone */}
        <input
          ref={videoInputRef}
          type="file"
          accept="video/mp4,video/webm,video/quicktime,.mov,.mp4,.webm,.avi"
          multiple
          onChange={handleVideoInputChange}
          className="hidden"
        />
        <div
          onDragOver={handleVideoDragOver}
          onDragLeave={handleVideoDragLeave}
          onDrop={handleVideoDrop}
          onClick={() => videoInputRef.current?.click()}
          className={`border-2 border-dashed rounded-lg p-4 text-center cursor-pointer transition-all ${
            isDraggingVideo 
              ? 'border-[#556B2F] bg-[#556B2F]/5' 
              : 'border-[#E5E0D5] hover:border-[#556B2F] hover:bg-[#F9F7F2]'
          } ${uploadingMedia || videos.length >= maxVideos ? 'opacity-50 pointer-events-none' : ''}`}
          data-testid="video-dropzone"
        >
          <Upload className={`w-6 h-6 mx-auto mb-2 ${isDraggingVideo ? 'text-[#556B2F]' : 'text-[#6B665E]'}`} />
          <p className="text-sm text-[#6B665E] font-['Nunito']">
            {isDraggingVideo ? 'Drop videos here' : 'Drag & drop videos or click to browse'}
          </p>
          <p className="text-xs text-[#6B665E]/70 font-['Nunito'] mt-1">
            MP4, WebM, MOV, AVI • Max 100MB each
          </p>
        </div>
      </div>

      {/* Upload Progress */}
      {uploadingMedia && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 sm:relative sm:inset-auto sm:bg-transparent sm:block sm:z-auto">
          <div className="bg-white rounded-xl p-6 sm:p-0 sm:rounded-none sm:bg-transparent shadow-lg sm:shadow-none">
            <div className="flex flex-col items-center gap-3 sm:flex-row sm:justify-between sm:gap-1">
              <div className="flex items-center gap-2 sm:gap-1">
                <Loader2 className="w-8 h-8 sm:w-3 sm:h-3 animate-spin text-[var(--liturgical-primary)]" />
                <span className="text-base sm:text-xs text-[#2D2A26] sm:text-[#6B665E] font-['Nunito']">
                  Uploading...
                </span>
              </div>
              <span className="text-xl sm:text-xs font-semibold sm:font-normal text-[var(--liturgical-primary)] sm:text-[#6B665E]">{uploadProgress}%</span>
            </div>
            <Progress value={uploadProgress} className="h-3 sm:h-2 mt-4 sm:mt-1 w-48 sm:w-full" />
          </div>
        </div>
      )}

      {/* Image Cropper Modal */}
      {imageToEdit && (
        <ImageCropperModal
          isOpen={cropperOpen}
          onClose={() => {
            setCropperOpen(false);
            setPendingImages([]);
            setImageToEdit(null);
          }}
          imageFile={imageToEdit}
          onSave={handleCroppedImage}
        />
      )}
    </div>
  );
}
